import { useMutation } from '@apollo/client';
import { useForm, UseFormReturnType } from '@mantine/form';
import { useState } from 'react';

import { sendResetPasswordEmail } from '@/api';
import { resolveGqlErrors } from '@/helpers';
import { isValidEmail, normalizeEmail } from '@/helpers/email';

export type FormValues = {
  email: string;
};

type UseForgotPasswordEmail = () => {
  onSubmit: () => void;
  form: UseFormReturnType<FormValues>;
  loading: boolean;
  error: string;
  success: boolean;
  reset: () => void;
};

const useForgotPasswordEmail: UseForgotPasswordEmail = () => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [sendEmail, { loading }] = useMutation(sendResetPasswordEmail);

  const form = useForm<FormValues>({
    initialValues: {
      email: '',
    },
    validate: {
      email: (value) => (isValidEmail(value) ? null : 'Please provide a valid email'),
    },
    transformValues: ({ email }) => ({ email: normalizeEmail(email) }),
  });

  const reset = () => {
    setSuccess(false);
    setError('');
    form.reset();
  };

  const onSubmit = form.onSubmit(async ({ email }) => {
    setError('');

    try {
      const { data } = await sendEmail({
        variables: {
          email,
          template: 'forgot_password',
        },
      });

      const errorMessage = resolveGqlErrors(data?.sendResetPasswordEmail?.error);

      if (errorMessage) {
        setError(errorMessage);
        return;
      }

      if (data?.sendResetPasswordEmail?.success) {
        setSuccess(true);
      } else {
        setError('Failed to send reset password email. Please verify your email and try again.');
      }
    } catch (err) {
      const errorMessage = resolveGqlErrors(err);
      setError(errorMessage || 'An unexpected error occurred. Please try again.');
    }
  });

  return {
    onSubmit,
    form,
    loading,
    error,
    success,
    reset,
  };
};

export default useForgotPasswordEmail;
